import { logError, addBreadcrumb } from './sentry-logger';
import {
    SPLIT_TEST_COOKIE_NAME,
    SPLIT_TEST_COOKIE_PATTERN,
    SPLIT_TEST_NETLIFY_PATTERN,
    SPLIT_TEST_COOKIE_EXPIRY_MONTHS,
} from './constants';

// Debug logging flag - only enabled for custom split test overrides (never for Netlify-assigned branches)
// This makes it clear to readers that console logs only appear during manual testing
let isDebugLoggingEnabled = false;

/**
 * Check if document is available for cookie operations
 * @private
 * @returns {boolean} Whether document is available
 */
const _isDocumentAvailable = () => {
    return typeof document !== 'undefined';
};

/**
 * Check if the split test cookie is present
 * @private
 * @returns {boolean} Whether the cookie is present
 */
const _isCookiePresent = () => {
    return document.cookie.includes(`${SPLIT_TEST_COOKIE_NAME}=`);
};

/**
 * Decode a cookie value, with fallback for corrupted values
 * @private
 * @param {string} encodedValue - The potentially encoded cookie value
 * @returns {string} The decoded value or the original if decoding fails
 */
const _decodeCookieValue = (encodedValue) => {
    try {
        // Decode the branch name that was encoded to prevent cookie injection
        // This handles branch names with special characters like ';', '=', '&', etc.
        return decodeURIComponent(encodedValue);
    } catch (e) {
        // If decoding fails (e.g., malformed or corrupted cookie value), return the raw value
        // This prevents the entire function from failing due to a corrupted cookie
        return encodedValue;
    }
};

/**
 * Clear the split test cookie and log the action
 * @private
 * @returns {boolean} Whether the cookie was successfully cleared
 */
const _clearSplitTestCookie = () => {
    // Enable debug logging when clearing a custom override
    isDebugLoggingEnabled = true;

    if (isDebugLoggingEnabled) {
        console.log('[Split Test] Clearing split test override');
    }

    // Clear the cookie by setting it with a past expiry date
    // Reuse the same function to ensure consistent attributes
    const pastExpiry = 'Thu, 01 Jan 1970 00:00:00 GMT';
    const cookieString = _createSplitTestCookieString('', pastExpiry);
    document.cookie = cookieString;

    // Verify the cookie is no longer present
    const cookieCleared = !_isCookiePresent();

    if (isDebugLoggingEnabled && cookieCleared) {
        console.log('[Split Test] Cookie cleared successfully');
    }

    if (isDebugLoggingEnabled && !cookieCleared) {
        console.warn('[Split Test] Failed to clear cookie');
    }

    // Log to Sentry
    addBreadcrumb({
        message: 'Split test override cleared',
        category: 'split-test',
        data: { success: cookieCleared },
    });

    return cookieCleared;
};

/**
 * Create a split test cookie string with the given branch name
 * @private
 * @param {string} splitTestParam - The branch name to set (empty string to clear)
 * @param {string} [expiryDate] - Optional expiry date string (defaults to future date, pass past date to clear)
 * @returns {string} The formatted cookie string
 */
const _createSplitTestCookieString = (splitTestParam, expiryDate) => {
    // Encode the branch name to prevent cookie injection attacks
    // This ensures special characters like ';' can't modify cookie attributes
    const safeValue = encodeURIComponent(splitTestParam);

    // Netlify accepts branch names directly in the nf_ab cookie
    // Set cookie matching Netlify's format
    let expires = expiryDate;
    if (!expires) {
        const futureDate = new Date();
        futureDate.setMonth(futureDate.getMonth() + SPLIT_TEST_COOKIE_EXPIRY_MONTHS);
        expires = futureDate.toUTCString();
    }

    // Since the app runs in an iframe (cross-site context), we need SameSite=None; Secure
    // This is required for the cookie to work in the iframe and to override Netlify's cookie
    // Without these attributes, the browser blocks the cookie in cross-site contexts
    const isHttps = window.location.protocol === 'https:';

    // For HTTPS (production/staging), use SameSite=None; Secure for iframe compatibility
    // For HTTP (local dev), omit these attributes since Secure requires HTTPS
    if (isHttps) {
        return `${SPLIT_TEST_COOKIE_NAME}=${safeValue};path=/;expires=${expires};SameSite=None;Secure`;
    }

    return `${SPLIT_TEST_COOKIE_NAME}=${safeValue};path=/;expires=${expires}`;
};

/**
 * Verify if the split test cookie was set successfully and log the result
 * @private
 * @param {string} splitTestParam - The branch name that was attempted to be set
 * @returns {boolean} Whether the cookie is present
 */
const _verifySplitTestCookie = (splitTestParam) => {
    // Verify the cookie is present
    const cookiePresent = _isCookiePresent();

    // Console logging only when debug mode is enabled
    if (isDebugLoggingEnabled && cookiePresent) {
        console.log('[Split Test] Cookie set successfully');
    }

    if (isDebugLoggingEnabled && !cookiePresent) {
        // Log failure with helpful suggestion
        // Use fallback for environments where origin might be undefined (e.g., older browsers, test environments)
        const origin =
            window.location.origin || `${window.location.protocol}//${window.location.host}`;
        const requestedUrl = `${origin}/?split-testing=${splitTestParam}`;
        console.warn('[Split Test] Failed to set cookie. Try accessing directly:');
        console.warn(requestedUrl);
    }

    // Always log to Sentry for tracking
    addBreadcrumb({
        message: 'Split test override activated',
        category: 'split-test',
        data: { branch: splitTestParam, success: cookiePresent },
    });

    return cookiePresent;
};

/**
 * Set the split test cookie with the given branch name
 * @private
 * @param {string} splitTestParam - The branch name to set
 * @returns {boolean} Whether the cookie was set successfully
 */
const _setSplitTestCookie = (splitTestParam) => {
    // Enable debug logging when setting a custom override
    isDebugLoggingEnabled = true;

    if (isDebugLoggingEnabled) {
        console.log('[Split Test] Setting override for branch:', splitTestParam);
    }

    const cookieString = _createSplitTestCookieString(splitTestParam);
    document.cookie = cookieString;

    return _verifySplitTestCookie(splitTestParam);
};

/**
 * Initialize split test override based on query parameters
 * This must be called before any network requests to Netlify
 *
 * @returns {boolean} Whether a split test override was applied
 */
export const initSplitTestOverride = () => {
    // Guard against environments where window might not be available
    const windowUnavailable = typeof window === 'undefined';
    if (windowUnavailable || !window.location) {
        return false;
    }

    try {
        const urlParams = new URLSearchParams(window.location.search);
        const splitTestParam = urlParams.get('split-testing');

        // Only run split test logic if the parameter is present
        if (!splitTestParam) {
            return false;
        }

        // Check if we should clear the override
        if (splitTestParam === 'clear') {
            _clearSplitTestCookie();
            return false;
        }

        // At this point, we have a valid split-testing parameter (not null, not 'clear')
        // Return the actual success status from setting the cookie
        return _setSplitTestCookie(splitTestParam);
    } catch (error) {
        // Error logging is always enabled for exceptions
        console.error('[Split Test] Error in initSplitTestOverride:', error);

        // Log error to Sentry
        logError(error, {
            tags: {
                source: 'split-test',
            },
        });

        return false;
    }
};

/**
 * Check if any split test is currently active (Netlify-assigned or manual override)
 *
 * @returns {boolean} Whether the nf_ab cookie is present
 */
export const isSplitTestActive = () => {
    if (!_isDocumentAvailable()) {
        return false;
    }
    return _isCookiePresent();
};

/**
 * Get the current split test branch name or value
 *
 * @returns {string|null} The current branch name, 'netlify-assigned' for numeric values, or null if no cookie
 */
export const getSplitTestBranch = () => {
    if (!_isDocumentAvailable()) {
        return null;
    }
    const match = document.cookie.match(SPLIT_TEST_COOKIE_PATTERN);
    if (match) {
        const encodedValue = match[1];

        // Check if it's a pure numeric value (Netlify's default) or an encoded branch name (our override)
        // Netlify assigns a numeric value between 0 and 1 (inclusive) for random A/B test assignments
        // These numeric values are not encoded, so we check the raw value first
        const isNetlifyRandomAssignment = SPLIT_TEST_NETLIFY_PATTERN.test(encodedValue);

        if (isNetlifyRandomAssignment) {
            // It's Netlify's random assignment - we don't know which branch without the split test config
            return 'netlify-assigned';
        }

        // Decode the branch name using the helper function
        return _decodeCookieValue(encodedValue);
    }
    return null;
};

/**
 * Check if the split test cookie was set by our override (not Netlify's random assignment)
 *
 * @returns {boolean} True if the cookie contains a branch name (not a numeric value)
 */
export const isSplitTestOverridden = () => {
    const branch = getSplitTestBranch();
    return !!(branch && branch !== 'netlify-assigned');
};
